/* eslint-disable import/order */
import React from 'react';
import { MenuIdProfile } from '@jira/navigation-apps__constants/src/MenuIdProfile.tsx';
import { SidebarIdPersonalSettings } from '@jira/navigation-apps__constants/src/SidebarIdPersonalSettings.tsx';
import type PersonalSettingsType from '@jira/spa-apps__personal-settings-legacy/src/PersonalSettingsShell.tsx';
import { CONSOLIDATION_STATE_RESOURCE_TYPE } from '@jira/router-resources__eoc-tenant-info/src/consolidationStateResourceType.constants.tsx';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { globalLayout } from '@jira/platform__layouts-global-layout/src/index.tsx';
import { getNavigationForgePersonalSettingsPageResources } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { GENERAL_PERSONAL_SETTINGS_PATH } from '@jira/personal-settings__paths/src/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { personalSettingsRoute } from '@jira/router-routes__personal-settings-routes/src/personalSettingsRoute.tsx';
import { ScreenRedirect } from '@jira/platform__routing-screen-redirect/src/ui/ScreenRedirect.tsx';
import { markAsProductBundleReady } from '@jira/platform__spa/src/services/mark-as-product-bundle-ready/index.tsx';
import {
	createNotificationPreferencesResource,
	eocUserResource,
	createForwardingRulesResource,
} from '@atlassian/eoc-notifications/resources';
import {
	createMuteResource,
	createQuietHoursResource,
} from '@atlassian/eoc-mute-quiet-hours/resources';
import { createContactsResource } from '@atlassian/eoc-contact-methods/resources';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { Route, RouteResources } from '@atlassian/react-resource-router';
import { getLabsResources } from './common/getLabsResources.tsx';
import { checkConsolidationOnRouteDependency } from './common/checkConsolidationOnRouteDependency.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyLegacyPersonalSettings = lazyForPaint<typeof PersonalSettingsType>(
	() =>
		markAsProductBundleReady(
			import(
				/* webpackChunkName: "async-personal-settings", jiraSpaEntry: "async-personal-settings" */ '@jira/spa-apps__personal-settings-legacy/src/PersonalSettingsShell.tsx'
			),
		),
	{
		ssr: false,
	},
);

export const personalSettingsRouteEntry: Route = createEntry(personalSettingsRoute, () => ({
	group: 'personal-settings',
	// eslint-disable-next-line no-restricted-syntax
	component: () => <ScreenRedirect to={GENERAL_PERSONAL_SETTINGS_PATH} />,
	layout: globalLayout,

	navigation: {
		sidebarId: SidebarIdPersonalSettings,
		menuId: MenuIdProfile,
	},

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	resources: [
		...getNavigationResources(),
		getConsolidationStateResource(),
		createContactsResource(checkConsolidationOnRouteDependency, [
			CONSOLIDATION_STATE_RESOURCE_TYPE,
		]),
		createNotificationPreferencesResource(checkConsolidationOnRouteDependency, [
			CONSOLIDATION_STATE_RESOURCE_TYPE,
		]),
		eocUserResource,
		createMuteResource(checkConsolidationOnRouteDependency, [CONSOLIDATION_STATE_RESOURCE_TYPE]),
		createQuietHoursResource(checkConsolidationOnRouteDependency, [
			CONSOLIDATION_STATE_RESOURCE_TYPE,
		]),
		createForwardingRulesResource(checkConsolidationOnRouteDependency, [
			CONSOLIDATION_STATE_RESOURCE_TYPE,
		]),
		...getNavigationForgePersonalSettingsPageResources(),
		...getLabsResources(),
	] as RouteResources,

	forPaint: [LazyAtlassianNavigation, LazyLegacyPersonalSettings],

	bifrost: {
		bifrostGroup: 'shell-only',
	},
}));
